import { useEffect, useState } from "react";
import useAnalyticsService from "../../../services/AnalyticsService";
import { DailySubscriptionAnalyticsItem, DailySubscriptionAnalyticsResult } from "../../../types/Analytics";
import {
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Ghost from "../../../components/Ghost";
import { ymd } from "../../../utilities/DateUtilities";
import { useUrlDateRange } from "../../../hooks/useUrlDateRange";
import DateRangeToolbar from "../../../components/DateRangeToolbar";
import { friendlyFormatNumber } from "../../../utilities/NumberUtilities";

export default function AdminAnalyticsSubscriptionsSummaryPage() {
  const { getDailySubscriptionAnalytics } = useAnalyticsService();
  const [isLoading, setIsLoading] = useState(false);

  const { startDate, endDate, setRange } = useUrlDateRange(7);

  const [dailyData, setDailyData] = useState<any[] | null>(null);

  useEffect(() => {
    if (!startDate || !endDate) return;
    getData();
  }, [startDate, endDate]);

  const getData = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const result = await getDailySubscriptionAnalytics(startDate!, endDate!);
      processData(result);
    } catch (error) {
      console.error("Error fetching subscription analytics:", error);
    }
    setIsLoading(false);
  };

  const processData = (result: DailySubscriptionAnalyticsResult) => {
    const dates = getDateList();

    const items: {
      date: Date;
      dateFriendlyName: string;
      activeCount: number;
      trialCount: number;
      cancelledCount: number;
      activeCountChange: number;
      trialCountChange: number;
      cancelledCountChange: number;
    }[] = dates.map((date) => ({
      date,
      dateFriendlyName: ymd(date),
      activeCount: 0,
      trialCount: 0,
      cancelledCount: 0,
      activeCountChange: 0,
      trialCountChange: 0,
      cancelledCountChange: 0,
    }));

    for (const row of result.items) {
      const rowDate = new Date(row.date);
      const item = items.find((i) => i.date.getUTCDate() === rowDate.getUTCDate());
      if (item) {
        item.activeCount = row.activeCount;
        item.trialCount = row.trialCount;
        item.cancelledCount = row.cancelledCount;
        item.activeCountChange = row.activeCountChange;
        item.trialCountChange = row.trialCountChange;
        item.cancelledCountChange = row.cancelledCountChange;
      }
    }

    setDailyData(items);
  };

  const getDateList = (): Date[] => {
    const dateList: Date[] = [];
    const current = new Date(startDate!);
    while (current <= endDate!) {
      dateList.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dateList;
  };

  const getLatest = (key: string) => {
    if (!dailyData || dailyData.length === 0) return 0;
    const last = [...dailyData].reverse().find((i) => i[key] !== 0);
    return last ? last[key] : dailyData[dailyData.length - 1][key];
  };

  const formatChange = (n: number) => {
    if (n > 0) return `+${n}`;
    return String(n);
  };

  return (
    <div className="grid grid-cols gap-4">
      <h2 className="text-2xl">Subscriptions</h2>
      <section className="mt-4">
        {startDate && endDate && (
          <DateRangeToolbar
            startDate={startDate}
            endDate={endDate}
            onChange={setRange}
          />
        )}
      </section>

      {!isLoading && dailyData && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-4">
            <h3 className="text-lg">Active subscribers (latest)</h3>
            <p className="text-3xl font-bold">{friendlyFormatNumber(getLatest("activeCount"))}</p>
          </div>
          <div className="card p-4">
            <h3 className="text-lg">Trial subscribers (latest)</h3>
            <p className="text-3xl font-bold">{friendlyFormatNumber(getLatest("trialCount"))}</p>
          </div>
          <div className="card p-4">
            <h3 className="text-lg">Cancelled (latest)</h3>
            <p className="text-3xl font-bold">{friendlyFormatNumber(getLatest("cancelledCount"))}</p>
          </div>
        </section>
      )}

      <section>
        <div className="grid grid-cols-1">
          <div className="w-full">
            <h3 className="text-lg">Subscription counts over time</h3>
            <div className="w-full h-64 md:h-80 min-w-0">
              {isLoading && <Ghost additionalClasses="w-full h-full" />}
              {!isLoading && dailyData && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyData}>
                    <Line
                      name="Active"
                      dataKey="activeCount"
                      stroke="#60a5fa"
                      dot={false}
                    />
                    <Line
                      name="Trial"
                      dataKey="trialCount"
                      stroke="#34d399"
                      dot={false}
                    />
                    <Line
                      name="Cancelled"
                      dataKey="cancelledCount"
                      stroke="#f87171"
                      dot={false}
                    />
                    <Legend />
                    <XAxis
                      dataKey="dateFriendlyName"
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <YAxis
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <Tooltip
                      labelStyle={{ color: "#000" }}
                      itemStyle={{ color: "#000" }}
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderColor: "#e5e7eb",
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
        <div className="p-4">
          {dailyData && (
            <table className="table-auto w-full card border-spacing-2">
              <thead>
                <tr>
                  <th className="font-normal text-left p-4">Date</th>
                  <th className="font-normal text-left p-4">Active</th>
                  <th className="font-normal text-left p-4">Active change</th>
                  <th className="font-normal text-left p-4">Trial</th>
                  <th className="font-normal text-left p-4">Trial change</th>
                  <th className="font-normal text-left p-4">Cancelled</th>
                  <th className="font-normal text-left p-4">Cancelled change</th>
                </tr>
              </thead>
              <tbody>
                {dailyData.map((item) => (
                  <tr
                    key={item.date.toString()}
                    className="border-t border-cyan-900 hover:bg-cyan-300/25"
                  >
                    <td className="p-4">{item.dateFriendlyName}</td>
                    <td className="p-4">{item.activeCount}</td>
                    <td className="p-4">{formatChange(item.activeCountChange)}</td>
                    <td className="p-4">{item.trialCount}</td>
                    <td className="p-4">{formatChange(item.trialCountChange)}</td>
                    <td className="p-4">{item.cancelledCount}</td>
                    <td className="p-4">{formatChange(item.cancelledCountChange)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
